import { useContext, useState } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { PrimaryButtonFitContainer } from "../../../components/Buttons/Buttons";
import { Notifications } from "../../../components/Notifications/NotificationsContext";
import { SearchSelectInfiniteScroll } from "../../../components/SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";
import { TextArea } from "../../../components/TextArea/TextArea";
import { endpoints } from "../../../endpoints";
import { Form } from "../../../layout/FormLayout";
import type { OptionType, Tenant, UUID } from "../../../types/types";
import { strings } from "../../../util/strings";
import {
  convertCustomerToOption,
  useFormWrapper,
  useStoreState,
} from "../../../util/util";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { zodOptionalString } from "../../../util/zod.util";

export type IForwardLead = {
  distributor: OptionType<UUID>;
  reasonForForwardLead: string;
};

type IForwardToDistributor = {
  handleForwardToDistributor: (data: IForwardLead) => Promise<void>;
};

export const ForwardToDistributor = ({
  handleForwardToDistributor,
}: IForwardToDistributor) => {
  const { t } = useTranslation();
  const { notifyError } = useContext(Notifications);
  const { tenant_id, storefront_id } = useStoreState();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const schema = z.object({
    distributor: z
      .union([
        z.object({
          value: z
            .string()
            .nonempty({ message: strings(t).thisIsARequiredField }),
          label: z.string().optional(),
        }),
        z.null(),
        z.undefined(),
      ])
      .refine((val) => val && val.value, {
        message: strings(t).thisIsARequiredField,
      }),
    reasonForForwardLead: zodOptionalString,
  });

  const { handleSubmit, setValue, errors, formState, register, control } =
    useFormWrapper({
      resolver: zodResolver(schema),
      defaultValues: {
        distributor: undefined,
        reasonForForwardLead: "",
      },
    });

  const onSubmit = async (data: IForwardLead) => {
    setIsSubmitting(true);
    try {
      await handleForwardToDistributor(data);
    } catch (error) {
      notifyError(t("Something went wrong while submitting the form."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const distributorParams = new URLSearchParams({
    order_by: "asc",
    tenant_type: "Distributor",
  });

  return (
    <>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          control={control}
          as={SearchSelectInfiniteScroll}
          name={"distributor"}
          theref={register({ required: true })}
          params={distributorParams}
          baseUrl={endpoints.v1_storefronts_id_tenants_id_tenants(
            storefront_id,
            tenant_id
          )}
          getOptions={(response: Tenant[]) =>
            response.map(convertCustomerToOption)
          }
          errors={errors}
          formState={formState}
          isClearable
          placeholder={t("Company Name")}
          onChange={(data: any) => {
            setValue("distributor", data);
          }}
        />

        <TextArea
          name={"reasonForForwardLead"}
          label={t("Message To Distributor")}
          theref={register({ required: false })}
          formState={formState}
          required={false}
          errors={errors}
        />

        <PrimaryButtonFitContainer type="submit" loading={isSubmitting}>
          {t("Forward")}
        </PrimaryButtonFitContainer>
      </Form>
    </>
  );
};
